import { isAxiosError } from 'axios';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { getApiErrorCode, getApiErrorMessage } from '@/lib/utils/error.utils';
import { EVENTS_STRINGS } from './constants/events-strings';
import { HTTP_STATUS_TOO_MANY_REQUESTS } from './constants/guest-registration';

const G = EVENTS_STRINGS.GUEST;

type GuestRegistrationFailure = 'not-accepting' | 'rate-limited' | 'network' | 'other';

const MESSAGE_BY_FAILURE: Record<GuestRegistrationFailure, string> = {
  'not-accepting': G.ERROR_NOT_ACCEPTING,
  'rate-limited': G.ERROR_RATE_LIMITED,
  network: G.ERROR_NETWORK,
  other: G.ERROR_GENERIC,
};

/** The two 400 refusals a member gets too: event not open, or registration closed. */
const NOT_ACCEPTING_CODES: ReadonlySet<string> = new Set([
  API_ERROR_CODES.EVENT_NOT_REGISTERABLE,
  API_ERROR_CODES.EVENT_REGISTRATION_CLOSED,
]);

function classify(error: unknown): GuestRegistrationFailure {
  const code = getApiErrorCode(error);
  if (code && NOT_ACCEPTING_CODES.has(code)) return 'not-accepting';
  if (!isAxiosError(error)) return 'other';
  if (error.response?.status === HTTP_STATUS_TOO_MANY_REQUESTS) return 'rate-limited';
  // No response at all: offline, DNS, CORS or the API is down.
  return error.response ? 'other' : 'network';
}

/**
 * Pure: the message the registration modal shows for a failed submission. The
 * messages are fixed copy and never echo the address, so nothing the visitor
 * typed can end up in an error string (NFR-32).
 */
export function guestRegistrationErrorMessage(error: unknown): string {
  return MESSAGE_BY_FAILURE[classify(error)];
}

/** The unregister failures with fixed copy; any other failure carries the API's own reason instead. */
const UNREGISTER_MESSAGE_BY_FAILURE: Partial<Record<GuestRegistrationFailure, string>> = {
  'rate-limited': G.UNREGISTER_ERROR_RATE_LIMITED,
  network: G.ERROR_NETWORK,
};

/**
 * Pure: the message the unregister dialog shows for a failed cancellation
 * (FR-289). A 429 says to try again shortly, whatever its body, and a request
 * that got no response (offline, DNS, CORS, a timeout) gets the user-facing
 * network message rather than axios's raw text such as `Network Error`. Any
 * other failure carries the API's own reason through `getApiErrorMessage`.
 * Never echoes the token or address.
 */
export function guestUnregisterErrorMessage(error: unknown): string {
  return UNREGISTER_MESSAGE_BY_FAILURE[classify(error)] ?? getApiErrorMessage(error, G.UNREGISTER_ERROR_GENERIC);
}
